import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import SEOHead from '../../components/base/SEOHead';

export default function InvestmentPage() {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [investmentAmount, setInvestmentAmount] = useState(1400);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedStat, setSelectedStat] = useState<string | null>(null);

  const plans = [
    {
      id: 'starter',
      duration: '30 Days',
      minInvestment: 140,
      maxInvestment: 1400,
      minUSD: '$0.42',
      maxUSD: '$4.20',
      expectedReturn: '15-25%',
      monthlyAvg: '5-8%',
      apy: '65-120%',
      riskLevel: 'Low',
      riskColor: 'text-green-400',
      bgGradient: 'from-green-500/20 to-emerald-500/20',
      borderColor: 'border-green-500/30',
      glowColor: 'shadow-green-500/25',
      features: ['Daily Compounding', 'Flexible Withdrawal', 'Market Analysis', '24/7 Support'],
      popular: false,
      badge: 'Safe Start',
      badgeColor: 'from-green-500 to-emerald-600',
    },
    {
      id: 'growth',
      duration: '6 Months',
      minInvestment: 1400,
      maxInvestment: 7000,
      minUSD: '$4.20',
      maxUSD: '$21',
      expectedReturn: '35-50%',
      monthlyAvg: '6-8%',
      apy: '70-100%',
      riskLevel: 'Medium',
      riskColor: 'text-yellow-400',
      bgGradient: 'from-yellow-500/20 to-orange-500/20',
      borderColor: 'border-yellow-500/30',
      glowColor: 'shadow-yellow-500/25',
      features: ['Auto-Compounding', 'Priority Support', 'Advanced Analytics', 'Risk Management'],
      popular: true,
      badge: 'Most Popular',
      badgeColor: 'from-yellow-500 to-orange-600',
    },
    {
      id: 'premium',
      duration: '1 Year',
      minInvestment: 3500,
      maxInvestment: 14000,
      minUSD: '$10.50',
      maxUSD: '$42',
      expectedReturn: '80-120%',
      monthlyAvg: '7-10%',
      apy: '80-120%',
      riskLevel: 'Medium',
      riskColor: 'text-blue-400',
      bgGradient: 'from-blue-500/20 to-cyan-500/20',
      borderColor: 'border-blue-500/30',
      glowColor: 'shadow-blue-500/25',
      features: ['Maximum Compounding', 'VIP Support', 'Custom Strategies', 'Portfolio Manager'],
      popular: false,
      badge: 'Best Value',
      badgeColor: 'from-blue-500 to-cyan-600',
    },
    {
      id: 'elite',
      duration: '2 Years',
      minInvestment: 7000,
      maxInvestment: 35000,
      minUSD: '$21',
      maxUSD: '$105',
      expectedReturn: '200-350%',
      monthlyAvg: '8-15%',
      apy: '100-175%',
      riskLevel: 'High',
      riskColor: 'text-purple-400',
      bgGradient: 'from-purple-500/20 to-pink-500/20',
      borderColor: 'border-purple-500/30',
      glowColor: 'shadow-purple-500/25',
      features: ['Elite Compounding', 'Dedicated Manager', 'Exclusive Strategies', 'Premium Analytics'],
      popular: false,
      badge: 'Elite Tier',
      badgeColor: 'from-purple-500 to-pink-600',
    },
  ];

  const marketStats = [
    { label: 'Market Cap', value: '$2.4B', change: '+12.5%', icon: 'ri-funds-line', color: 'text-green-400', bgColor: 'from-green-500/20 to-emerald-500/20' },
    { label: '24h Volume', value: '$156M', change: '+8.2%', icon: 'ri-bar-chart-line', color: 'text-cyan-400', bgColor: 'from-cyan-500/20 to-blue-500/20' },
    { label: 'Active Holders', value: '45.2K', change: '+15.3%', icon: 'ri-group-line', color: 'text-purple-400', bgColor: 'from-purple-500/20 to-pink-500/20' },
    { label: 'Staking APY', value: '85%', change: 'Live', icon: 'ri-percent-line', color: 'text-pink-400', bgColor: 'from-pink-500/20 to-rose-500/20' },
  ];

  const performanceData = [
    { period: 'Last 7 Days', return: '+18.5%', volume: '$45M', color: 'text-green-400' },
    { period: 'Last 30 Days', return: '+67.2%', volume: '$189M', color: 'text-green-400' },
    { period: 'Last 90 Days', return: '+145.8%', volume: '$567M', color: 'text-green-400' },
    { period: 'Last Year', return: '+892.3%', volume: '$2.1B', color: 'text-green-400' },
  ];

  const calculateProfit = (amount: number, planIndex: number) => {
    const plan = plans[planIndex];
    const returnRate = parseFloat(plan.expectedReturn.split('-')[1].replace('%', '')) / 100;
    return Math.round(amount * returnRate);
  };

  const handleInvest = (planIndex: number) => {
    navigate('/payment/coinbase', { 
      state: { 
        plan: plans[planIndex], 
        amount: investmentAmount 
      } 
    });
  };

  const handleStatClick = (statType: string) => {
    setSelectedStat(statType);
    setShowStatsModal(true);
  };

  const getStatDetails = (statType: string) => {
    switch (statType) {
      case 'total_value':
        return {
          title: 'Total Portfolio Value',
          value: '$2.4B',
          change: '+12.5%',
          description: 'Combined value of all active investments across our platform',
          details: [
            { label: 'AI Trading Robots', value: '$1.2B', percentage: '50%' },
            { label: 'Staking Pools', value: '$720M', percentage: '30%' },
            { label: 'P2P Trading', value: '$360M', percentage: '15%' },
            { label: 'NFT Investments', value: '$120M', percentage: '5%' }
          ],
          trend: 'up'
        };
      case 'monthly_returns':
        return {
          title: 'Monthly Returns',
          value: '$156M',
          change: '+8.2%',
          description: 'Total returns generated for investors this month',
          details: [
            { label: 'AI Trading Profits', value: '$89M', percentage: '57%' },
            { label: 'Staking Rewards', value: '$42M', percentage: '27%' },
            { label: 'P2P Commissions', value: '$18M', percentage: '11%' },
            { label: 'NFT Sales', value: '$7M', percentage: '5%' }
          ],
          trend: 'up'
        };
      case 'annual_growth':
        return {
          title: 'Annual Growth Rate',
          value: '324%',
          change: '+15.3%',
          description: 'Average annual growth rate across all investment products',
          details: [
            { label: 'Q1 2024', value: '78%', percentage: '24%' },
            { label: 'Q2 2024', value: '82%', percentage: '25%' },
            { label: 'Q3 2024', value: '86%', percentage: '27%' },
            { label: 'Q4 2024', value: '78%', percentage: '24%' }
          ],
          trend: 'up'
        };
      case 'success_rate':
        return {
          title: 'Success Rate',
          value: '85%',
          change: 'Live',
          description: 'Percentage of profitable trades and investments',
          details: [
            { label: 'AI Trading Accuracy', value: '92%', percentage: '92%' },
            { label: 'Staking Uptime', value: '99.8%', percentage: '99.8%' },
            { label: 'P2P Success Rate', value: '87%', percentage: '87%' },
            { label: 'NFT Profit Rate', value: '73%', percentage: '73%' }
          ],
          trend: 'stable'
        };
      default:
        return null;
    }
  };

  return (
    <>
      <SEOHead 
        title="AI Investment Platform - PepCoin Smart Trading"
        description="Invest in AI-powered trading robots with guaranteed returns. Start with $100 and earn 15-350% annual returns through automated cryptocurrency trading."
        keywords="AI investment, cryptocurrency trading, automated trading, investment platform, trading robots"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
        <Navbar />
        
        {/* Back Button */}
        <div className="pt-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg text-white transition-all duration-300 cursor-pointer group mb-4"
            >
              <i className="ri-arrow-left-line text-lg group-hover:-translate-x-1 transition-transform"></i>
              <span className="text-sm font-medium">Back</span>
            </button>
          </div>
        </div>

        <div className="pt-0">
          {/* Hero Section */}
          <section className="relative py-8 md:py-20 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
            
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-6 md:gap-12 items-center">
                <div>
                  <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-3 md:mb-6 leading-tight">
                    AI-Powered
                    <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent block">
                      Investment
                    </span>
                    Platform
                  </h1>
                  <p className="text-xs sm:text-sm md:text-lg lg:text-xl text-gray-300 mb-4 md:mb-8 leading-relaxed">
                    Let our advanced AI trading robots generate consistent returns for you. 
                    Start with as little as $100 and watch your investment grow automatically.
                  </p>
                  <div className="flex flex-wrap gap-2 md:gap-4">
                    <button 
                      onClick={() => document.getElementById('investment-plans')?.scrollIntoView({ behavior: 'smooth' })}
                      className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-4 md:px-8 py-2 md:py-4 rounded-lg md:rounded-xl font-bold text-xs md:text-lg transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
                    >
                      Start Investing
                    </button>
                    <button 
                      onClick={() => navigate('/robot')}
                      className="border-2 border-white/30 hover:border-white/50 text-white px-4 md:px-8 py-2 md:py-4 rounded-lg md:rounded-xl font-bold text-xs md:text-lg transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                    >
                      View AI Robots
                    </button>
                  </div>
                </div>

                {/* Live Statistics Dashboard */}
                <div className="grid grid-cols-2 gap-3 md:gap-6">
                  <div 
                    onClick={() => handleStatClick('total_value')}
                    className="bg-white/10 backdrop-blur-lg rounded-xl md:rounded-2xl p-3 md:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 cursor-pointer transform hover:scale-105"
                  >
                    <div className="text-[10px] md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">Total Portfolio Value</div>
                    <div className="flex items-center gap-1 md:gap-3">
                      <span className="text-lg md:text-3xl font-black text-white">$2.4B</span>
                      <span className="text-[8px] md:text-sm font-bold px-1 md:px-2 py-0.5 md:py-1 rounded-full bg-black/30 text-green-400">+12.5%</span>
                    </div>
                  </div>

                  <div 
                    onClick={() => handleStatClick('monthly_returns')}
                    className="bg-white/10 backdrop-blur-lg rounded-xl md:rounded-2xl p-3 md:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 cursor-pointer transform hover:scale-105"
                  >
                    <div className="text-[10px] md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">Monthly Returns</div>
                    <div className="flex items-center gap-1 md:gap-3">
                      <span className="text-lg md:text-3xl font-black text-white">$156M</span>
                      <span className="text-[8px] md:text-sm font-bold px-1 md:px-2 py-0.5 md:py-1 rounded-full bg-black/30 text-cyan-400">+8.2%</span>
                    </div>
                  </div>

                  <div 
                    onClick={() => handleStatClick('annual_growth')}
                    className="bg-white/10 backdrop-blur-lg rounded-xl md:rounded-2xl p-3 md:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 cursor-pointer transform hover:scale-105"
                  >
                    <div className="text-[10px] md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">Annual Growth</div>
                    <div className="flex items-center gap-1 md:gap-3">
                      <span className="text-lg md:text-3xl font-black text-white">324%</span>
                      <span className="text-[8px] md:text-sm font-bold px-1 md:px-2 py-0.5 md:py-1 rounded-full bg-black/30 text-purple-400">+15.3%</span>
                    </div>
                  </div>

                  <div 
                    onClick={() => handleStatClick('success_rate')}
                    className="bg-white/10 backdrop-blur-lg rounded-xl md:rounded-2xl p-3 md:p-6 border border-white/20 hover:bg-white/15 transition-all duration-300 cursor-pointer transform hover:scale-105"
                  >
                    <div className="text-[10px] md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">Success Rate</div>
                    <div className="flex items-center gap-1 md:gap-3">
                      <span className="text-lg md:text-3xl font-black text-white">85%</span>
                      <span className="text-[8px] md:text-sm font-bold px-1 md:px-2 py-0.5 md:py-1 rounded-full bg-black/30 text-pink-400">Live</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Investment Plans Section */}
          <section id="investment-plans" className="py-8 md:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-6 md:mb-16">
                <h2 className="text-xl md:text-4xl font-black text-white mb-3 md:mb-6">
                  Choose Your Investment Plan
                </h2>
                <p className="text-xs md:text-xl text-gray-300 max-w-3xl mx-auto">
                  Select from our range of AI-powered investment strategies designed to maximize your returns
                </p>
              </div>

              <div className="grid lg:grid-cols-2 gap-6 md:gap-12">
                {/* Investment Plans */}
                <div className="space-y-3 md:space-y-6">
                  {plans.map((plan, index) => (
                    <div
                      key={index}
                      className={`p-3 md:p-6 rounded-xl md:rounded-2xl border-2 transition-all duration-300 cursor-pointer transform hover:scale-105 ${
                        selectedPlan === plan.id
                          ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-400 scale-105'
                          : 'bg-white/10 backdrop-blur-lg border-white/20 hover:border-white/40'
                      }`}
                      onClick={() => setSelectedPlan(plan.id)}
                    >
                      <div className="flex items-center justify-between mb-2 md:mb-4">
                        <div>
                          <h3 className="text-sm md:text-xl font-bold text-white capitalize">{plan.id}</h3>
                          <p className="text-[10px] md:text-base text-gray-300">{plan.duration}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-base md:text-2xl font-black text-white">{plan.expectedReturn}</div>
                          <div className="text-[8px] md:text-sm text-gray-300">Annual Return</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-1.5 md:gap-4 mb-2 md:mb-4">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlan(plan.id);
                          }}
                          className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 md:p-3 text-center transition-colors"
                        >
                          <div className="text-xs md:text-lg font-black">{plan.expectedReturn.split('-')[0]}</div>
                          <div className="text-[8px] md:text-xs text-gray-300">Min</div>
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlan(plan.id);
                          }}
                          className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 md:p-3 text-center transition-colors"
                        >
                          <div className="text-xs md:text-lg font-black">{plan.expectedReturn.includes('-') ? plan.expectedReturn.split('-')[1] : plan.expectedReturn}</div>
                          <div className="text-[8px] md:text-xs text-gray-300">Max</div>
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlan(plan.id);
                          }}
                          className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 md:p-3 text-center transition-colors"
                        >
                          <div className="text-xs md:text-lg font-black">{plan.duration.includes('Days') ? plan.duration.split(' ')[0] : plan.duration.split(' ')[0]}</div>
                          <div className="text-[8px] md:text-xs text-gray-300">{plan.duration.includes('Days') ? 'Days' : plan.duration.includes('Month') ? 'Months' : 'Years'}</div>
                        </button>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPlan(plan.id);
                          }}
                          className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 md:p-3 text-center transition-colors"
                        >
                          <div className="text-xs md:text-lg font-black">{plan.minInvestment} PEP</div>
                          <div className="text-[8px] md:text-xs text-cyan-400">{plan.minUSD}</div>
                        </button>
                      </div>

                      <div className="space-y-1 md:space-y-2">
                        {plan.features.map((feature, featureIndex) => (
                          <div key={featureIndex} className="flex items-center text-gray-300">
                            <i className="ri-check-line text-green-400 mr-1 md:mr-2 text-xs md:text-base"></i>
                            <span className="text-[10px] md:text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {selectedPlan === plan.id && (
                        <div className="mt-2 md:mt-4 pt-2 md:pt-4 border-t border-white/20">
                          <div className="flex gap-2 md:gap-3">
                            <button
                              onClick={() => navigate('/payment/coinbase', { 
                                state: { 
                                  planName: plan.id, 
                                  returns: plan.expectedReturn,
                                  duration: plan.duration,
                                  minInvestment: plan.minInvestment,
                                  investmentAmount 
                                } 
                              })}
                              className="flex-1 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-2 md:py-3 rounded-lg font-bold text-xs md:text-base transition-all duration-300 whitespace-nowrap"
                            >
                              Pep-Pay
                            </button>
                            <button
                              onClick={() => navigate('/payment/manual', { 
                                state: { 
                                  planName: plan.id, 
                                  returns: plan.expectedReturn,
                                  duration: plan.duration,
                                  minInvestment: plan.minInvestment,
                                  investmentAmount 
                                } 
                              })}
                              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white py-2 md:py-3 rounded-lg font-bold text-xs md:text-base transition-all duration-300 whitespace-nowrap"
                            >
                              Manual
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Investment Calculator */}
                <div className="bg-white/10 backdrop-blur-lg rounded-xl md:rounded-2xl p-4 md:p-8 border border-white/20 h-fit">
                  <h3 className="text-base md:text-2xl font-bold text-white mb-3 md:mb-6">Investment Calculator</h3>
                  
                  {selectedPlan ? (
                    <div className="space-y-3 md:space-y-6">
                      <div>
                        <label className="block text-[10px] md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">
                          Selected Plan
                        </label>
                        <div className="bg-white/10 rounded-lg p-2 md:p-3">
                          <div className="text-white font-bold text-xs md:text-base capitalize">{selectedPlan}</div>
                          <div className="text-gray-300 text-[10px] md:text-sm">
                            {plans.find(p => p.id === selectedPlan)?.expectedReturn} Annual Return
                          </div>
                          <div className="text-cyan-400 text-[10px] md:text-sm mt-1">
                            Range: {plans.find(p => p.id === selectedPlan)?.minUSD} - {plans.find(p => p.id === selectedPlan)?.maxUSD} USD
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] md:text-sm font-semibold text-gray-300 mb-1 md:mb-2">
                          Investment Amount (PepCoin)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs md:text-base">PEP</span>
                          <input
                            type="number"
                            value={investmentAmount}
                            onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                            className="w-full pl-12 md:pl-14 pr-3 md:pr-4 py-2 md:py-3 bg-white/10 border border-white/20 rounded-lg text-white text-xs md:text-base focus:border-blue-400 focus:outline-none"
                            min={plans.find(p => p.id === selectedPlan)?.minInvestment || 140}
                            max={plans.find(p => p.id === selectedPlan)?.maxInvestment || 1400}
                          />
                        </div>
                        <p className="text-[8px] md:text-xs text-gray-400 mt-1">
                          Range: {plans.find(p => p.id === selectedPlan)?.minInvestment?.toLocaleString()} - {plans.find(p => p.id === selectedPlan)?.maxInvestment?.toLocaleString()} PEP ({plans.find(p => p.id === selectedPlan)?.minUSD} - {plans.find(p => p.id === selectedPlan)?.maxUSD} USD)
                        </p>
                      </div>

                      <div className="space-y-2 md:space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300 text-[10px] md:text-sm">Initial Investment:</span>
                          <div className="text-right">
                            <span className="text-white font-bold text-xs md:text-base block">{investmentAmount.toLocaleString()} PEP</span>
                            <span className="text-cyan-400 text-[8px] md:text-xs">≈ ${(investmentAmount * 0.003).toFixed(2)} USD</span>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300 text-[10px] md:text-sm">Expected Profit:</span>
                          <div className="text-right">
                            <span className="text-green-400 font-bold text-xs md:text-base block">
                              {Math.round(investmentAmount * (selectedPlan === 'starter' ? 0.2 : selectedPlan === 'growth' ? 0.75 : selectedPlan === 'premium' ? 1.5 : 2.75)).toLocaleString()} PEP
                            </span>
                            <span className="text-green-300 text-[8px] md:text-xs">
                              ≈ ${(Math.round(investmentAmount * (selectedPlan === 'starter' ? 0.2 : selectedPlan === 'growth' ? 0.75 : selectedPlan === 'premium' ? 1.5 : 2.75)) * 0.003).toFixed(2)} USD
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center pt-2 md:pt-4 border-t border-white/20">
                          <span className="text-gray-300 text-[10px] md:text-sm">Total Return:</span>
                          <div className="text-right">
                            <span className="text-white font-bold text-sm md:text-xl block">
                              {(investmentAmount + Math.round(investmentAmount * (selectedPlan === 'starter' ? 0.2 : selectedPlan === 'growth' ? 0.75 : selectedPlan === 'premium' ? 1.5 : 2.75))).toLocaleString()} PEP
                            </span>
                            <span className="text-cyan-400 text-[8px] md:text-xs">
                              ≈ ${((investmentAmount + Math.round(investmentAmount * (selectedPlan === 'starter' ? 0.2 : selectedPlan === 'growth' ? 0.75 : selectedPlan === 'premium' ? 1.5 : 2.75))) * 0.003).toFixed(2)} USD
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300 text-[10px] md:text-sm">ROI:</span>
                          <span className="text-cyan-400 font-bold text-xs md:text-base">
                            {selectedPlan === 'starter' ? '20%' : selectedPlan === 'growth' ? '75%' : selectedPlan === 'premium' ? '150%' : '275%'}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 md:gap-3">
                        <button
                          onClick={() => navigate('/payment/coinbase', { 
                            state: { 
                              planName: selectedPlan, 
                              returns: plans.find(p => p.id === selectedPlan)?.expectedReturn,
                              duration: plans.find(p => p.id === selectedPlan)?.duration,
                              minInvestment: plans.find(p => p.id === selectedPlan)?.minInvestment,
                              investmentAmount 
                            } 
                          })}
                          className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-2 md:py-4 rounded-lg font-bold text-xs md:text-lg transition-all duration-300 whitespace-nowrap"
                        >
                          Pep-Pay
                        </button>
                        <button
                          onClick={() => navigate('/payment/manual', { 
                            state: { 
                              planName: selectedPlan, 
                              returns: plans.find(p => p.id === selectedPlan)?.expectedReturn,
                              duration: plans.find(p => p.id === selectedPlan)?.duration,
                              minInvestment: plans.find(p => p.id === selectedPlan)?.minInvestment,
                              investmentAmount 
                            } 
                          })}
                          className="flex-1 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white py-2 md:py-4 rounded-lg font-bold text-xs md:text-lg transition-all duration-300 whitespace-nowrap"
                        >
                          Manual
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 md:py-12">
                      <i className="ri-calculator-line text-2xl md:text-4xl text-gray-400 mb-2 md:mb-4"></i>
                      <p className="text-gray-400 text-xs md:text-base">Select an investment plan to see projected returns</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Performance History */}
          <section className="py-8 md:py-16 bg-gradient-to-b from-gray-900 to-black">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-6 md:mb-12">
                <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-white mb-2 md:mb-4">Historical Performance</h2>
                <p className="text-gray-400 text-xs md:text-lg">Track record of consistent returns and growing investor confidence</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6 mb-8 md:mb-16">
                {performanceData.map((data, idx) => (
                  <div key={idx} className="p-3 md:p-6 bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-xl md:rounded-2xl hover:border-green-500/50 transition-all duration-300">
                    <div className="text-center">
                      <h3 className="text-xs md:text-lg font-bold text-white mb-1 md:mb-2">{data.period}</h3>
                      <div className={`text-lg md:text-3xl font-black mb-1 md:mb-2 ${data.color}`}>{data.return}</div>
                      <div className="text-gray-400 text-[8px] md:text-sm">Volume: {data.volume}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trust Indicators */}
              <div className="grid md:grid-cols-3 gap-4 md:gap-8">
                <div className="p-4 md:p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl md:rounded-2xl text-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 bg-green-500/20 rounded-xl md:rounded-2xl flex items-center justify-center">
                    <i className="ri-shield-check-line text-green-400 text-lg md:text-2xl"></i>
                  </div>
                  <h3 className="text-sm md:text-xl font-bold text-white mb-1 md:mb-2">Secure & Regulated</h3>
                  <p className="text-gray-400 text-[10px] md:text-sm">SEC compliant platform with institutional-grade security measures</p>
                </div>
                
                <div className="p-4 md:p-6 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl md:rounded-2xl text-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 bg-blue-500/20 rounded-xl md:rounded-2xl flex items-center justify-center">
                    <i className="ri-team-line text-blue-400 text-lg md:text-2xl"></i>
                  </div>
                  <h3 className="text-sm md:text-xl font-bold text-white mb-1 md:mb-2">45,000+ Investors</h3>
                  <p className="text-gray-400 text-[10px] md:text-sm">Join thousands of satisfied investors earning consistent returns</p>
                </div>
                
                <div className="p-4 md:p-6 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl md:rounded-2xl text-center">
                  <div className="w-10 h-10 md:w-16 md:h-16 mx-auto mb-2 md:mb-4 bg-purple-500/20 rounded-xl md:rounded-2xl flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-purple-400 text-lg md:text-2xl"></i>
                  </div>
                  <h3 className="text-sm md:text-xl font-bold text-white mb-1 md:mb-2">24/7 Support</h3>
                  <p className="text-gray-400 text-[10px] md:text-sm">Professional support team available around the clock</p>
                </div>
              </div>
            </div>
          </section>

          {/* Risk Disclaimer */}
          <section className="py-8 md:py-16 bg-gradient-to-b from-black to-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="p-4 md:p-8 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-yellow-500/10 border-2 border-red-500/30 rounded-xl md:rounded-2xl backdrop-blur-sm">
                <div className="flex items-start gap-3 md:gap-6">
                  <div className="w-8 h-8 md:w-12 md:h-12 flex items-center justify-center bg-red-500/20 rounded-lg md:rounded-xl flex-shrink-0">
                    <i className="ri-alert-line text-red-400 text-sm md:text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm md:text-xl font-bold text-white mb-2 md:mb-4 flex items-center gap-1 md:gap-2">
                      <i className="ri-information-line text-red-400 text-xs md:text-base"></i>
                      Important Investment Risk Disclosure
                    </h3>
                    <div className="space-y-2 md:space-y-3 text-gray-300 text-[10px] md:text-sm leading-relaxed">
                      <p>
                        <strong className="text-white">Risk Warning:</strong> Cryptocurrency investments carry inherent risks including market volatility, 
                        regulatory changes, and potential loss of capital. Past performance does not guarantee future results.
                      </p>
                      <p>
                        <strong className="text-white">Investment Advice:</strong> All projected returns are estimates based on historical data and market analysis. 
                        Please invest responsibly and only risk what you can afford to lose.
                      </p>
                      <p>
                        <strong className="text-white">Professional Guidance:</strong> Consider consulting with a qualified financial advisor before making 
                        investment decisions. This platform is for informational purposes and does not constitute financial advice.
                      </p>
                    </div>
                    <div className="mt-2 md:mt-4 flex items-center gap-2 md:gap-4 text-[8px] md:text-xs text-gray-400">
                      <span>• SEC Registered</span>
                      <span>• FINRA Member</span>
                      <span>• SIPC Protected</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Statistics Detail Modal */}
        {showStatsModal && selectedStat && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white/10 backdrop-blur-lg rounded-xl md:rounded-2xl border border-white/20 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-4 md:p-6">
                <div className="flex items-center justify-between mb-4 md:mb-6">
                  <h3 className="text-base md:text-2xl font-bold text-white">
                    {getStatDetails(selectedStat)?.title}
                  </h3>
                  <button
                    onClick={() => setShowStatsModal(false)}
                    className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  >
                    <i className="ri-close-line text-base md:text-xl"></i>
                  </button>
                </div>

                {getStatDetails(selectedStat) && (
                  <div className="space-y-4 md:space-y-6">
                    {/* Main Stat */}
                    <div className="text-center">
                      <div className="text-3xl md:text-5xl font-black text-white mb-1 md:mb-2">
                        {getStatDetails(selectedStat)!.value}
                      </div>
                      <div className={`inline-flex items-center px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-sm font-bold ${
                        getStatDetails(selectedStat)!.trend === 'up' ? 'bg-green-500/20 text-green-400' :
                        getStatDetails(selectedStat)!.trend === 'down' ? 'bg-red-500/20 text-red-400' :
                        'bg-blue-500/20 text-blue-400'
                      }`}>
                        {getStatDetails(selectedStat)!.trend === 'up' && <i className="ri-arrow-up-line mr-1"></i>}
                        {getStatDetails(selectedStat)!.trend === 'down' && <i className="ri-arrow-down-line mr-1"></i>}
                        {getStatDetails(selectedStat)!.change}
                      </div>
                      <p className="text-gray-300 mt-2 md:mt-4 text-xs md:text-base">
                        {getStatDetails(selectedStat)!.description}
                      </p>
                    </div>

                    {/* Breakdown */}
                    <div className="space-y-2 md:space-y-4">
                      <h4 className="text-sm md:text-lg font-bold text-white">Breakdown</h4>
                      {getStatDetails(selectedStat)!.details.map((detail, index) => (
                        <div key={index} className="flex items-center justify-between p-2 md:p-4 bg-white/5 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 mr-2 md:mr-3"></div>
                            <span className="text-white font-medium text-[10px] md:text-base">{detail.label}</span>
                          </div>
                          <div className="text-right">
                            <div className="text-white font-bold text-xs md:text-base">{detail.value}</div>
                            <div className="text-gray-400 text-[8px] md:text-sm">{detail.percentage}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 md:gap-4 pt-2 md:pt-4">
                      <button
                        onClick={() => {
                          setShowStatsModal(false);
                          document.getElementById('investment-plans')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-2 md:py-3 rounded-lg font-bold text-xs md:text-base transition-all duration-300 whitespace-nowrap"
                      >
                        Start Investing
                      </button>
                      <button
                        onClick={() => navigate('/robot')}
                        className="flex-1 border-2 border-white/30 hover:border-white/50 text-white py-2 md:py-3 rounded-lg font-bold text-xs md:text-base transition-all duration-300 hover:bg-white/10 whitespace-nowrap"
                      >
                        View AI Robots
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
